import { action, computed, observable, reaction, autorun } from 'mobx';
import axios from 'axios';
import isPlainObject from 'lodash/isPlainObject';

// import consts
import { defaultOfferSuiteId, ghPreselectedPlan, ghPreselectedAnnualTerm } from '~/common/globals.js';
import { PLAN_TERM_MONTHLY, PLAN_TERM_ANNUAL, TRUE_SOLO_EXTENSION, SOLO_PLUS_EXTENSION, PARTNER_EXTENSION, SMALL_BUSINESS_EXTENSION, DEFAULT_EXTENSION, TRUE_SOLO_USERS, DEFAULT_USERS, OFFER_SUITE_WITH_TRUE_SOLO, PLANS_PRICE_INCREASE_ENABLED, DEFAULT_FORWARD_NUMBERS, TRUE_SOLO_FORWARD_NUMBERS } from '~/common/utils/consts.js';

class OfferSuiteClass {
    @observable fetchedOffers = false;
    @observable fetchedOffersError = false;
    @observable offerSuite;
    @observable selectedPlanTerm = PLAN_TERM_ANNUAL;
    @observable selectedOfferSet;
    @observable planPreselected = false;

    bootstrapStore() {
        this.initOfferSuiteFetch();

        // upon successful fetching of offers
        reaction(
            () => this.fetchedOffers,
            () => {
                // if offersuite doest have annual plans and the selected plan term is annual, switch back to monthly
                if (!this.hasAnnualPlans && this.selectedPlanTerm === PLAN_TERM_ANNUAL) this.setSelectedPlanTerm();

                // if offer is preselected
                if (ghPreselectedPlan()) {
                    const preselectedOffer = decodeURI(ghPreselectedPlan());
                    // find offer id in paired Plans by term
                    const foundOfferSet = this.pairedPlansByTerm.find((offer) => {
                        return preselectedOffer.toLowerCase() === offer[this.selectedPlanTerm].Name.toLowerCase();
                    });
                    if (foundOfferSet) {
                        this.setSelectedOfferSet(foundOfferSet);
                        this.setPlanPreselected(true);
                    }
                }
            },
        );
    }

    // Initiating OfferSuite call
    initOfferSuiteFetch() {
        // Get Offer Suite ID
        let offerSuiteId = defaultOfferSuiteId();
        // If new price flag enabled
        if (PLANS_PRICE_INCREASE_ENABLED) {
            // Use new OfferSuite
            offerSuiteId = OFFER_SUITE_WITH_TRUE_SOLO;
        }
        this.getOfferSuite(offerSuiteId);
        const preselectedAnnualTerm = ghPreselectedAnnualTerm();
        // set user's preselected plan term
        autorun((rctn) => {
            // if the user picked plan term monthly and the set plan term is annual switch it to monthly
            if (preselectedAnnualTerm && preselectedAnnualTerm.toLowerCase() === 'false' && this.selectedPlanTerm === PLAN_TERM_ANNUAL) {
                this.setSelectedPlanTerm();
                rctn.dispose();
            }
        });
    }

    // MANIPULATE OFFER SUITE BEFORE ADDING IT TO THE STORE
    addOfferSuiteInfo(suite) {
        const offerSuite = suite;
        // loop through the offers and add needed info
        offerSuite.Offers.map((plan, idx) => {
            const { Term, RecurringCharge, MinutesInOfferCharge, Name } = plan;
            // SET MONTHLY COST
            // if there is a recurring charge and minute charge
            if (typeof RecurringCharge === 'number' && typeof MinutesInOfferCharge === 'number') {
                // set a new key that adds the two
                offerSuite.Offers[idx].monthlyPlanCost = RecurringCharge + MinutesInOfferCharge;

                if (Term.toLowerCase() === PLAN_TERM_ANNUAL) offerSuite.Offers[idx].monthlyPlanCost /= 12;
            }
            // SET NUMBER OF EXTENSIONS
            let includedExtensions;

            // Set number of Users
            let includedUsers;

            // Set number of forwarding numbers
            let includedForwardingNumbers;

            // set included extensions based on plan name
            switch (Name) {
                case 'True Solo':
                    includedExtensions = TRUE_SOLO_EXTENSION;
                    includedUsers = TRUE_SOLO_USERS;
                    includedForwardingNumbers = TRUE_SOLO_FORWARD_NUMBERS;
                    break;
                case 'Solo Plus':
                    includedExtensions = SOLO_PLUS_EXTENSION;
                    includedUsers = DEFAULT_USERS;
                    includedForwardingNumbers = DEFAULT_FORWARD_NUMBERS;
                    break;
                case 'Partner':
                    // FIXME: remove afer pricing test is over PLANS D
                    includedExtensions = PARTNER_EXTENSION;
                    includedUsers = DEFAULT_USERS;
                    includedForwardingNumbers = DEFAULT_FORWARD_NUMBERS;
                    break;
                case 'Small Business':
                    includedExtensions = SMALL_BUSINESS_EXTENSION;
                    includedUsers = DEFAULT_USERS;
                    includedForwardingNumbers = DEFAULT_FORWARD_NUMBERS;
                    break;
                default:
                    includedExtensions = DEFAULT_EXTENSION;
                    includedUsers = DEFAULT_USERS;
                    includedForwardingNumbers = DEFAULT_FORWARD_NUMBERS;
            }
            // add included extensions key to offer
            offerSuite.Offers[idx].IncludedExtensions = includedExtensions;

            // add included users key to offer
            offerSuite.Offers[idx].IncludedUsers = includedUsers;

            // add included forwarding numbers capacity to offer
            offerSuite.Offers[idx].IncludedForwardingNumbers = includedForwardingNumbers;

            // SET VOICEMAIL TRANSCRIPTION
            offerSuite.Offers[idx].VoicemailTranscriptionIncluded = true;

            // otherwise don't add the key to the offer
            return false;
        });
        // sort offer suites by price
        offerSuite.Offers.sort((a, b) => {
            return a.monthlyPlanCost - b.monthlyPlanCost;
        });

        return offerSuite;
    }

    // get offer suite from API
    @action getOfferSuite(offerSuiteId = defaultOfferSuiteId(), callBack) {
        // only get new offer suite if you dont already have one
        // or if you're getting a different offer suite
        if (!this.offerSuite || offerSuiteId !== this.offerSuiteId) {
            // reset flags
            this.setFetchedOffers(false);
            this.setFetchedOffersError(false);
            // get a list of offers
            axios.get(`/api/OfferSuite/${offerSuiteId}`)
                .then((response) => {
                    // set offer suite with response data
                    const offerSuite = response.data;
                    // if any issues with the offer suite
                    if (!offerSuite && offerSuite.Offers.length <= 0) throw new Error('no offers in the offer suite');
                    // add monthly prices, extensions, and read your voicemail. and sort offers by price before adding them to the store
                    const finishedOfferSuite = this.addOfferSuiteInfo(offerSuite);

                    this.setOfferSuite(finishedOfferSuite);
                    // set flag that we fetched offers
                    this.setFetchedOffers(true);
                    // if callback
                    if (callBack) callBack();
                }).catch((err) => {
                // catch errors from sources other than API
                // set fetched offers to false
                    this.setFetchedOffers(false);
                    this.setFetchedOffersError(true);
                    // return new Error('there was an error');
                    throw err;
                });
        }
    }

    @action setSelectedPlanTerm() {
        this.selectedPlanTerm = this.selectedPlanTerm === PLAN_TERM_ANNUAL ? PLAN_TERM_MONTHLY : PLAN_TERM_ANNUAL;
    }

    // set flag if plan is preselected
    @action setPlanPreselected(bool) {
        // if true/false
        if (typeof bool === 'boolean') {
            this.planPreselected = bool;
        }
    }

    // set flag if offers have been fetched
    @action setFetchedOffers(bool) {
        // if true/false
        if (typeof bool === 'boolean') {
            // set fetchedOffers flag
            this.fetchedOffers = bool;
        }
    }

    // set flag if offers have been fetched
    @action setFetchedOffersError(bool) {
        // if true/false
        if (typeof bool === 'boolean') {
            // set fetchedOffersError flag
            this.fetchedOffersError = bool;
        }
    }

    // set selected offer
    @action setSelectedOfferSet(obj) {
        // if id is object (guid)
        if (isPlainObject(obj)) {
            // set id
            this.selectedOfferSet = obj;
        } else if (obj === null) {
            // set id to default undefined value
            this.selectedOfferSet = undefined;
        }
    }

    // set fetched offer suite
    @action setOfferSuite(offerSuite) {
        // if offerSuite is object
        if (isPlainObject(offerSuite)) {
            // set offer suite
            this.offerSuite = offerSuite;
        }
    }

    @computed get offerName() {
        if (isPlainObject(this.offerSuite) && this.selectedOfferSet && this.selectedOfferSet[this.selectedPlanTerm].Name) {
            return this.selectedOfferSet[this.selectedPlanTerm].Name;
        }
        return false;
    }

    // FIXME: remove afer pricing test is over PLANS C
    @computed get usersPerAccount() {
        if (isPlainObject(this.offerSuite) && this.selectedOfferSet && this.selectedOfferSet[this.selectedPlanTerm].includedUsers) {
            return this.selectedOfferSet[this.selectedPlanTerm].includedUsers;
        }
        return false;
    }

    @computed get offerRecurringCharge() {
        if (isPlainObject(this.offerSuite) && this.selectedOfferSet && this.selectedOfferSet[this.selectedPlanTerm].monthlyPlanCost) {
            return this.selectedOfferSet[this.selectedPlanTerm].monthlyPlanCost;
        }
        return false;
    }

    @computed get monthlyOfferRecurringCharge() {
        if (isPlainObject(this.offerSuite) && this.selectedOfferSet && this.selectedOfferSet[PLAN_TERM_MONTHLY].monthlyPlanCost) {
            return this.selectedOfferSet[PLAN_TERM_MONTHLY].monthlyPlanCost;
        }
        return false;
    }

    // return SMSPerNumberCharge
    @computed get smsPerNumberCharge() {
        if (isPlainObject(this.offerSuite) && this.selectedOfferSet && this.selectedOfferSet[this.selectedPlanTerm].SMSPerNumberCharge) {
            return this.selectedOfferSet[this.selectedPlanTerm].SMSPerNumberCharge;
        }
        return false;
    }

    @computed get selectedOfferId() {
        if (isPlainObject(this.offerSuite) && this.selectedOfferSet && this.selectedOfferSet[this.selectedPlanTerm].Id) {
            return this.selectedOfferSet[this.selectedPlanTerm].Id;
        }
        return false;
    }

    // get offer suite Id
    @computed get offerSuiteId() {
        // if offer suite exists and has an id
        if (isPlainObject(this.offerSuite) && this.offerSuite.Id) {
            // return offer suite id
            return this.offerSuite.Id;
        }
        return false;
    }

    // get default offer
    @computed get defaultOffer() {
        // if offer suite exists and has an default offer name
        if (isPlainObject(this.offerSuite) && this.offerSuite.DefaultOfferName) {
            // return default offer name
            return this.offerSuite.DefaultOfferName;
        }
        return false;
    }

    // check if offer term is monthly or annual
    @computed get isTermMonthly() {
        return this.selectedPlanTerm === PLAN_TERM_MONTHLY;
    }

    @computed get isTermAnnual() {
        return this.selectedPlanTerm === PLAN_TERM_ANNUAL;
    }

    // find out if the offer has annual plans
    @computed get hasAnnualPlans() {
        const offerSuite = this.offerSuite;
        // if offerSuite is object and has offers
        if (isPlainObject(offerSuite) && offerSuite.Offers.length > 0) {
            // find an offer suite with term === plan
            return offerSuite.Offers.find((offer) => {
                return offer.Term.toLowerCase() === PLAN_TERM_ANNUAL;
            });
        }
        return false;
    }

    // create array of offers pared by type
    @computed get pairedPlansByTerm() {
        // grab offer suite
        const offerSuite = this.offerSuite;
        // if offer suite exist and has offers
        if (isPlainObject(offerSuite) && offerSuite.Offers.length > 0) {
            const { Offers } = offerSuite;
            return Offers.reduce((offArray, offer) => {
                // if offer is annual
                if (offer.Term.toLowerCase() === PLAN_TERM_MONTHLY) {
                    // find matching monthly offer suite
                    const matchAnnualOffer = Offers.find((offr) => {
                        return offr.Name === offer.Name && offr.Term.toLowerCase() === PLAN_TERM_ANNUAL;
                    });
                    // if matching offer suite found, create obj with both of them
                    const offerObj = {};
                    offerObj[PLAN_TERM_MONTHLY] = offer;
                    offerObj[PLAN_TERM_ANNUAL] = matchAnnualOffer || false;
                    offArray.push(offerObj);
                }
                return offArray;
            }, []);
        }

        return false;
    }
}

const offerSuiteStore = new OfferSuiteClass();
export { offerSuiteStore, OfferSuiteClass };
